import { useNavigate } from '@tanstack/react-router';
import { ChevronsUpDown, Languages, LogOut, Monitor, Moon, Sun } from 'lucide-react';

import { signOut, useSession } from '@/shared/auth';
import { language, setLanguage, t, type Language } from '@/shared/i18n';
import { cx } from '@/shared/lib/cx';
import { useTheme, type ThemeMode } from '@/shared/lib/useTheme';
import { Avatar } from '@/shared/ui/Avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/DropdownMenu';
import { toast } from '@/shared/ui/Toast';

import styles from './UserMenu.module.scss';

/**
 * Меню человека внизу колонки: тема, язык, выход — как во flow-do.
 *
 * Тема — здесь, а не кнопкой в шапке: у неё три состояния (как в системе,
 * тёмная, светлая), а кнопка-переключатель умеет показать только два.
 */
export function UserMenu({ compact = false }: { compact?: boolean }) {
  const session = useSession();
  const navigate = useNavigate();
  const { mode, setMode } = useTheme();

  if (session.status !== 'authenticated') {
    return null;
  }

  const { user } = session;
  const name = user.name === '' ? user.email : user.name;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cx(styles['trigger'], compact && styles['trigger--compact'])}
          aria-label={compact ? name : undefined}
        >
          <Avatar name={name} size="sm" />
          {!compact && (
            <>
              <span className={styles['trigger__text']}>
                <span className={styles['trigger__name']}>{name}</span>
                <span className={styles['trigger__email']}>{user.email}</span>
              </span>
              <ChevronsUpDown className={styles['trigger__chevron']} />
            </>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" side="top" sideOffset={8}>
        <DropdownMenuLabel>{t.userMenu.theme}</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={mode}
          onValueChange={(value) => {
            setMode(value as ThemeMode);
          }}
        >
          <DropdownMenuRadioItem value="system" icon={<Monitor />}>
            {t.userMenu.themeSystem}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark" icon={<Moon />}>
            {t.userMenu.themeDark}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="light" icon={<Sun />}>
            {t.userMenu.themeLight}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>

        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t.userMenu.language}</DropdownMenuLabel>
        {/* Названия языков — на самих языках: человек, случайно включивший
            чужой, должен узнать свой, не умея читать текущий. */}
        <DropdownMenuRadioGroup
          value={language}
          onValueChange={(value) => {
            setLanguage(value as Language);
          }}
        >
          <DropdownMenuRadioItem value="ru" icon={<Languages />}>
            Русский
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="en" icon={<Languages />}>
            English
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>

        <DropdownMenuSeparator />
        <DropdownMenuItem
          icon={<LogOut />}
          onSelect={() => {
            signOut()
              .catch(() => {
                toast.error(t.userMenu.signOutFailed);
              })
              .finally(() => {
                void navigate({ to: '/login' });
              });
          }}
        >
          {t.userMenu.signOut}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
